import React, { useEffect, useState } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { personColor, clickable } from './utils';

const API = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

const SCOPES = [
  { id: 'open',  label: 'Still open', hint: 'Maintenance Tasks not yet closed' },
  { id: 'total', label: 'All',        hint: 'Open and closed Maintenance Tasks' },
];

export default function MaintenanceLoadPage({ uploadId, teamOnly }) {
  const [data, setData]   = useState(null);
  const [error, setError] = useState(null);
  const [scope, setScope] = useState('open');
  const [selected, setSelected] = useState(null);
  const toggle = (name) => setSelected(s => (s === name ? null : name));

  useEffect(() => {
    const qs = new URLSearchParams({ team_only: teamOnly ? 'true' : 'false' });
    if (uploadId) qs.set('upload_id', uploadId);
    fetch(`${API}/api/planning/maintenance-load?${qs}`)
      .then(async r => { const d = await r.json(); if (!r.ok) throw new Error(d.detail || 'Could not load the maintenance load'); return d; })
      .then(d => { setData(d); setError(null); })
      .catch(e => setError(e.message));
  }, [uploadId, teamOnly]);

  const people = (data?.people || [])
    .map(p => ({ ...p, value: p[scope] }))
    .filter(p => p.value > 0)
    .sort((a, b) => b.value - a.value || a.assignee.localeCompare(b.assignee));
  const total = people.reduce((sum, p) => sum + p.value, 0);
  const share = (v) => (total ? Math.round((v / total) * 100) : 0);
  const top = people[0];
  const scopeInfo = SCOPES.find(s => s.id === scope);
  // Hidden automatically when the scope switch removes the selected person.
  const selectedPerson = people.find(p => p.assignee === selected);
  const customers = selectedPerson ? (selectedPerson[`customers_${scope}`] || []) : [];

  return (
    <div>
      <div className="card card-spaced-lg" style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <div role="group" aria-label="Which tasks to count" style={{ display: 'flex', gap: 6 }}>
          {SCOPES.map(s => (
            <button key={s.id} type="button" aria-pressed={scope === s.id}
              className={`btn ${scope === s.id ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setScope(s.id)}>{s.label}</button>
          ))}
        </div>
        <span style={{ fontSize: 12, color: 'var(--text-2)' }}>
          {scopeInfo.hint} per assignee{teamOnly ? ', team only' : ''}, from the latest sync. Canceled tasks are not counted.
        </span>
      </div>

      {error && <div className="card" role="alert" style={{ color: 'var(--critical)', marginBottom: 12 }}>{error}</div>}

      {!error && data && people.length === 0 && (
        <div className="card" style={{ color: 'var(--text-2)' }}>No Maintenance Tasks to show for this selection.</div>
      )}

      {people.length > 0 && (
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'stretch' }}>
          <div className="card" style={{ flex: '1 1 340px', minWidth: 0 }}>
            <div className="section-title">Who carries the most maintenances</div>
            {top && (
              <div style={{ fontSize: 13, margin: '6px 0 4px' }}>
                <strong style={{ color: personColor(top.assignee) }}>{top.assignee}</strong>
                {' '}— {top.value} task{top.value === 1 ? '' : 's'} ({share(top.value)}% of {total})
              </div>
            )}
            <div style={{ width: '100%', height: 320 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={people} dataKey="value" nameKey="assignee" innerRadius={70} outerRadius={125}
                    paddingAngle={1} isAnimationActive={false}
                    label={({ value }) => `${share(value)}%`}
                    onClick={(_, index) => toggle(people[index].assignee)}>
                    {people.map(p => (
                      <Cell key={p.assignee} fill={personColor(p.assignee)} style={{ cursor: 'pointer' }}
                        fillOpacity={!selected || selected === p.assignee ? 1 : 0.35} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v, name) => [`${v} (${share(v)}%)`, name]} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="card" style={{ flex: '1 1 340px', minWidth: 0 }}>
            <div className="section-title">Per technician</div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr><th>Technician</th><th style={{ textAlign: 'right' }}>Open</th><th style={{ textAlign: 'right' }}>Closed</th><th style={{ textAlign: 'right' }}>Total</th><th style={{ textAlign: 'right' }}>Share</th></tr>
                </thead>
                <tbody>
                  {people.map(p => (
                    <tr key={p.assignee} {...clickable(() => toggle(p.assignee))}
                      aria-pressed={selected === p.assignee} aria-label={`List customers of ${p.assignee}`}
                      style={{ cursor: 'pointer', background: selected === p.assignee ? 'var(--surface-2)' : undefined }}>
                      <td>
                        <span aria-hidden="true" style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, marginRight: 8, background: personColor(p.assignee) }} />
                        {p.assignee}
                      </td>
                      <td className="mono" style={{ textAlign: 'right' }}>{p.open}</td>
                      <td className="mono" style={{ textAlign: 'right' }}>{p.done}</td>
                      <td className="mono" style={{ textAlign: 'right' }}>{p.total}</td>
                      <td className="mono" style={{ textAlign: 'right', fontWeight: 600 }}>{share(p.value)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {people.length > 0 && !selectedPerson && (
        <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 10 }}>
          Click a slice or a row to list that technician's customers.
        </div>
      )}

      {selectedPerson && (
        <div className="card" style={{ marginTop: 16 }} aria-live="polite">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div className="section-title" style={{ margin: 0 }}>
              <span style={{ color: personColor(selectedPerson.assignee) }}>{selectedPerson.assignee}</span> — customers
            </div>
            <button type="button" className="btn btn-ghost" style={{ marginLeft: 'auto', fontSize: 12 }}
              onClick={() => setSelected(null)}>Close</button>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-2)', margin: '4px 0 10px' }}>
            {customers.length} customer{customers.length === 1 ? '' : 's'} with {scope === 'open' ? 'open' : 'open or closed'} Maintenance Tasks
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.7 }}>
            {customers.map(c => <li key={c}>{c}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}
